import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Package, AlertTriangle, Check, ShoppingCart } from "lucide-react";

/**
 * Banner cu piese de schimb necesare/comandate.
 * Vizibil pentru admin și magazioner.
 * Se reîncarcă la fiecare 60s.
 */
export function SparePartsBanner() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState({}); // {defect_id: action}
  const allowed = !!user && (user.role === "admin" || user.role === "magazioner");

  const load = async () => {
    if (!allowed) return;
    setLoading(true);
    try {
      const r = await api.get("/cars/spare-parts/orders");
      setOrders(r.data || []);
    } catch { /* noop */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [allowed]);

  const orderPart = async (o) => {
    setBusy((b) => ({ ...b, [o.defect_id]: "ordering" }));
    try {
      await api.post(`/cars/${o.car_id}/defects/${o.defect_id}/order-part`);
      toast.success(`📦 Piesa „${o.spare_part_name}" marcată ca COMANDATĂ`);
      load();
    } catch { toast.error("Eroare la marcarea comandei"); }
    finally { setBusy((b) => { const c = { ...b }; delete c[o.defect_id]; return c; }); }
  };

  const receivePart = async (o) => {
    setBusy((b) => ({ ...b, [o.defect_id]: "receiving" }));
    try {
      await api.post(`/cars/${o.car_id}/defects/${o.defect_id}/receive-part`);
      toast.success(`✅ Piesa „${o.spare_part_name}" intrată în stoc (+1)`);
      load();
    } catch { toast.error("Eroare la primirea piesei"); }
    finally { setBusy((b) => { const c = { ...b }; delete c[o.defect_id]; return c; }); }
  };

  if (!allowed || loading) return null;
  if (!orders || orders.length === 0) return null;

  const toOrder = orders.filter((o) => o.spare_part_status === "needed");
  const ordered = orders.filter((o) => o.spare_part_status === "ordered");

  return (
    <div className="space-y-2 mb-3" data-testid="spare-parts-banner">
      {toOrder.length > 0 && (
        <div className="rounded-lg border-2 border-red-400 bg-gradient-to-r from-red-50 to-orange-50 p-3 shadow-md animate-pulse-red-soft">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="text-red-600" size={18} />
            <h3 className="font-extrabold text-red-700 text-sm uppercase tracking-wide">
              ⚠ Comandă piese: {toOrder.length} {toOrder.length === 1 ? "piesă" : "piese"} necesare
            </h3>
          </div>
          <div className="space-y-1.5">
            {toOrder.map((o) => (
              <div
                key={o.defect_id}
                className="flex items-start gap-2 bg-white/60 rounded-md p-2 border border-red-200"
                data-testid={`spare-to-order-${o.defect_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-red-900">
                    🔩 {o.spare_part_name}
                    <span className="ml-2 text-xs font-normal text-zinc-600">
                      pentru <button onClick={() => nav(`/autoturisme/${o.car_id}`)} className="text-blue-700 hover:underline font-semibold">{o.car_label}</button>
                    </span>
                  </div>
                  <div className="text-[11px] text-zinc-500 truncate">
                    Defect: {o.defect_reason} · raportat de {o.reported_by || "—"}
                  </div>
                </div>
                <button
                  onClick={() => orderPart(o)}
                  disabled={!!busy[o.defect_id]}
                  className="text-[11px] px-2.5 py-1 rounded font-bold bg-orange-500 hover:bg-orange-600 text-white inline-flex items-center gap-1 disabled:opacity-50"
                  data-testid={`spare-mark-ordered-${o.defect_id}`}
                >
                  <ShoppingCart size={12} /> Comandată
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {ordered.length > 0 && (
        <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Package className="text-amber-700" size={18} />
            <h3 className="font-extrabold text-amber-800 text-sm uppercase tracking-wide">
              📦 Piese comandate (așteaptă livrare): {ordered.length}
            </h3>
          </div>
          <div className="space-y-1.5">
            {ordered.map((o) => (
              <div
                key={o.defect_id}
                className="flex items-start gap-2 bg-white/70 rounded-md p-2 border border-amber-200"
                data-testid={`spare-ordered-${o.defect_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-amber-900">
                    📦 {o.spare_part_name}
                    <span className="ml-2 text-xs font-normal text-zinc-600">
                      pentru <button onClick={() => nav(`/autoturisme/${o.car_id}`)} className="text-blue-700 hover:underline font-semibold">{o.car_label}</button>
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => receivePart(o)}
                  disabled={!!busy[o.defect_id]}
                  className="text-[11px] px-2.5 py-1 rounded font-bold bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1 disabled:opacity-50"
                  data-testid={`spare-mark-received-${o.defect_id}`}
                >
                  <Check size={12} /> Primită → în stoc
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
